/* Gedrag van de pagina "Mijn wijnen": bestelformulier, verwijderen en sorteren.
   De lijst zelf komt server-side uit het company-metafield; dit script raakt
   die rendering niet aan.

   Module in plaats van classic script, omdat de winkelwagenwijziging via het
   bestaande CartUpdateEvent van Horizon wordt gemeld (assets/cart-icon.js
   luistert daarop) en het ordertotaal met dezelfde formatMoney wordt opgemaakt
   als de prijzen per rij. */
import { CartUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { activeStaffelLevel, rowTotalCents } from '@theme/mijnwijnen-staffel';

const TOGGLE_ENDPOINT = '/apps/mijnwijnen/toggle';
const LEAVING_MS = 160;

const routes =
  typeof Theme !== 'undefined' && Theme.routes
    ? Theme.routes
    : { cart_add_url: '/cart/add.js', cart_url: '/cart' };

const send = (productId) =>
  fetch(TOGGLE_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ product_id: Number(productId) }),
  }).then((response) => response.json());

/* De service togglet en kent onze bedoeling niet. De bedoelde eindtoestand is
   hier altijd in_list: false. Antwoordt de server met true, dan stond de wijn
   er niet meer (een collega was ons voor) en heeft de toggle hem juist
   toegevoegd; dan volgt precies één correctiecall. Zonder die correctie
   verdwijnt de rij netjes uit beeld terwijl de wijn op de lijst terugkomt, en
   dat merkt niemand. Nooit meer dan één correctie: anders kunnen twee browsers
   elkaar in een lus houden. */
const removeFromList = async (productId) => {
  let data = await send(productId);
  if (!data.ok) throw data;
  if (data.in_list) {
    data = await send(productId);
    if (!data.ok) throw data;
    if (data.in_list) throw { ok: false, error: 'upstream_error' };
  }
  return data;
};

/* Meldt de wijziging aan de teller in de header. Het contract staat beschreven
   in assets/mijnwijnen-indicator.js. Verwijderen levert daar geen paneel op,
   alleen een teller die terugtelt: wie zelf een wijn weghaalt, hoeft niet te
   horen dat dat gelukt is. Zonder deze melding zou de teller te hoog blijven
   staan tot de volgende paginalading. */
const notify = (detail) => {
  document.dispatchEvent(new CustomEvent('mijnwijnen:changed', { detail }));
};

const init = (root) => {
  const form = root.querySelector('[data-mijnwijnen-orderform]');
  const list = root.querySelector('[data-mijnwijnen-rows]');
  /* Geen formulier = een lege of geblokkeerde state; daar valt niets te doen. */
  if (!form || !list) return;

  const feedback = root.querySelector('[data-mijnwijnen-feedback]');
  const summaryMain = root.querySelector('[data-summary-main]');
  const summarySub = root.querySelector('[data-summary-sub]');
  const totalElement = root.querySelector('[data-summary-total]');
  const totalNote = root.querySelector('[data-summary-note]');
  const submit = form.querySelector('.mijnwijnen__submit');
  const listCount = root.querySelector('[data-mijnwijnen-listcount]');
  const sortSelect = root.querySelector('[data-mijnwijnen-sort]');
  const resetButton = root.querySelector('[data-mijnwijnen-reset]');

  const moneyFormat = root.dataset.moneyFormat || '{{amount}}';
  const currency = root.dataset.currency || 'EUR';
  /* data-price staat in centen exclusief btw, precies zoals money-ex-vat ze
     levert. Rekenen in centen, pas bij het tonen formatteren: zo is een
     afrondingsfout in het ordertotaal onmogelijk. */
  const money = (cents) => formatMoney(cents, moneyFormat, currency);

  /* "Eigen volgorde" is de volgorde waarin de accountmanager de lijst heeft
     opgebouwd; die leggen we bij het initialiseren vast. */
  const manualOrder = new WeakMap();
  Array.from(list.children).forEach((row, index) => manualOrder.set(row, index));

  const rows = () => Array.from(list.querySelectorAll('[data-mijnwijnen-row]'));
  const isLeaving = (row) => row.classList.contains('mijnwijnen__row--leaving') || row.style.display === 'none';

  /* ---- meldingen: één live-region voor alle vijf de soorten ---------- */

  const say = (kind, text, action) => {
    if (!feedback) return;
    feedback.className = `mijnwijnen__feedback mijnwijnen__feedback--visible mijnwijnen__feedback--${kind}`;
    const inner = document.createElement('span');
    inner.className = 'mijnwijnen__feedback-inner';
    inner.append(text);
    if (action) inner.append(action);
    feedback.replaceChildren(inner);
  };

  const clearSay = () => {
    if (!feedback) return;
    feedback.className = 'mijnwijnen__feedback';
    feedback.replaceChildren();
  };

  const actionButton = (label, onClick) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'mijnwijnen__feedback-action';
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
  };

  const cartLink = () => {
    const link = document.createElement('a');
    link.href = routes.cart_url;
    link.textContent = 'Naar de winkelwagen';
    return link;
  };

  /* ---- staffel per rij ------------------------------------------------ */

  /* Eén keer parsen en onthouden: refresh() draait bij elke toetsaanslag in een
     aantalveld en er staan tot 30 rijen op de pagina. Een kapotte payload mag de
     hele lijst niet meenemen in zijn val; die rij gedraagt zich dan als een rij
     zonder staffel. */
  const staffelCache = new WeakMap();

  const staffelFor = (row) => {
    if (staffelCache.has(row)) return staffelCache.get(row);
    let data = null;
    const payload = row.querySelector('[data-mijnwijnen-staffel]');
    if (payload) {
      try {
        const parsed = JSON.parse(payload.textContent);
        /* basePrice moet er net zo goed zijn als de niveaus: renderStaffel zet
           die string terug in de prijscel zodra het aantal onder de laagste
           drempel zakt. Ontbreekt hij, dan zou de cel op dat moment leeglopen;
           liever gedraagt de rij zich dan meteen als een rij zonder staffel. */
        if (
          parsed &&
          Array.isArray(parsed.levels) &&
          parsed.levels.length > 0 &&
          typeof parsed.basePrice === 'string' &&
          parsed.basePrice !== ''
        ) {
          data = parsed;
        }
      } catch (error) {
        console.warn('[mijnwijnen] kon de staffeldata van een rij niet lezen', error);
      }
    }
    staffelCache.set(row, data);
    return data;
  };

  /* De prijsstrings komen server-side uit money-ex-vat en het money format van
     deze winkel is kale tekst (geverifieerd op de storefront), dus textContent
     en geen innerHTML. De sectie haalt datzelfde format al door strip_html voor
     de totaalbalk; hiermee staan beide paden op één lijn. Zet de winkel ooit de
     valutaweergave met HTML aan, dan moet dit terug naar innerHTML, samen met de
     strip_html-filters in sections/mijnwijnen-list.liquid.

     refresh() draait bij elke toetsaanslag over alle rijen, dus elke schrijfactie
     die niets verandert staat achter een gelijkheidscheck: zonder dat herschrijft
     één toets tot 30 rijen aan tekst en attributen voor niets. toggleAttribute en
     classList.toggle met een force-waarde schrijven volgens de DOM-spec al niet
     als de toestand klopt, die hebben geen extra check nodig. */
  const renderStaffel = (row, staffel, level) => {
    const priceElement = row.querySelector('[data-mijnwijnen-price]');
    const baseElement = row.querySelector('[data-mijnwijnen-price-base]');
    const badgeElement = row.querySelector('[data-mijnwijnen-price-badge]');

    if (priceElement) {
      const price = level ? level.price : staffel.basePrice;
      if (priceElement.textContent !== price) priceElement.textContent = price;
    }
    /* Bij een reguliere actie blijft de compare-at-prijs ook zonder actief
       staffelniveau zichtbaar. Zonder actie is dit element de staffel-
       basisprijs en verschijnt het pas zodra een staffelkorting geldt. */
    baseElement?.toggleAttribute('hidden', !level && !staffel.hasCompareAt);
    if (badgeElement) {
      if (level) {
        const badge = `−${level.pctLabel}%`;
        if (badgeElement.textContent !== badge) badgeElement.textContent = badge;
      }
      badgeElement.toggleAttribute('hidden', !level);
    }

    const chips = Array.from(row.querySelectorAll('[data-mijnwijnen-chip]'));
    if (chips.length === 0) return;
    /* Beide kanten door Number(): levert de payload ooit strings, dan zou
       '6' === 6 onwaar zijn en markeert geen enkele chip zich als actief.
       activeStaffelLevel doet dit sinds f71bdec al aan de keuzekant. Zonder
       geldend niveau blijft activeQty null, en daar matcht geen enkel getal op.

       Bij een tussenliggend aantal is geen chip actief, maar de groep houdt wel
       één tabstop; anders is hij met het toetsenbord niet meer te bereiken. Dat
       wordt vóór de lus bepaald: als achteraf, dan gaat chips[0] eerst naar -1
       en in dezelfde render weer terug naar 0, bij elke toetsaanslag opnieuw. */
    const activeQty = level ? Number(level.qty) : null;
    const hasActive = chips.some((chip) => Number(chip.dataset.quantity) === activeQty);
    for (const chip of chips) {
      const isActive = Number(chip.dataset.quantity) === activeQty;
      const checked = isActive ? 'true' : 'false';
      if (chip.getAttribute('aria-checked') !== checked) chip.setAttribute('aria-checked', checked);
      chip.classList.toggle('mijnwijnen__chip--active', isActive);
      const tabIndex = isActive || (!hasActive && chip === chips[0]) ? 0 : -1;
      if (chip.tabIndex !== tabIndex) chip.tabIndex = tabIndex;
    }
  };

  /* ---- samenvatting en ordertotaal ----------------------------------- */

  const refresh = () => {
    let bottles = 0;
    let wineCount = 0;
    let totalCents = 0;
    let visibleRows = 0;
    let staffelRows = 0;

    for (const row of rows()) {
      const hidden = isLeaving(row);
      if (!hidden) visibleRows += 1;

      const input = row.querySelector('[data-mijnwijnen-qty]');
      if (!input) continue;

      const quantity = Number(input.value) || 0;
      const staffel = staffelFor(row);
      const levels = staffel ? staffel.levels : [];
      const level = activeStaffelLevel(levels, quantity);

      if (!hidden && quantity > 0) {
        bottles += quantity;
        wineCount += 1;
        /* Aantal maal de geldende stukprijs in centen. Nooit een percentage op
           een bedrag: die stukprijzen komen als centen uit Liquid. Shopify past
           de korting in de checkout op de regel toe, dus dit totaal kan een cent
           afwijken; de microcopy onder het totaal zegt dat ook. */
        totalCents += rowTotalCents(levels, quantity, Number(input.dataset.price) || 0);
        if (level) staffelRows += 1;
      }

      if (staffel) renderStaffel(row, staffel, level);

      const stepper = input.closest('.mijnwijnen__stepper');
      stepper?.classList.toggle('mijnwijnen__stepper--filled', quantity > 0);
      stepper?.querySelector('[data-delta="-1"]')?.toggleAttribute('disabled', quantity === 0);
    }

    if (summaryMain) {
      summaryMain.textContent =
        bottles === 0 ? '0 flessen geselecteerd' : `${bottles} ${bottles === 1 ? 'fles' : 'flessen'} geselecteerd`;
    }
    if (summarySub) {
      summarySub.textContent =
        bottles === 0 ? 'Vul hieronder aantallen in' : `uit ${wineCount} ${wineCount === 1 ? 'wijn' : 'wijnen'}`;
    }
    if (totalElement) {
      totalElement.hidden = bottles === 0;
      const value = totalElement.querySelector('b');
      if (value) value.textContent = money(totalCents);
    }
    /* Geen voorbehoud tonen als er niets verrekend is: dan roept de zin alleen
       vragen op. */
    if (totalNote) totalNote.hidden = staffelRows === 0;
    if (listCount) {
      listCount.textContent = `${visibleRows} ${visibleRows === 1 ? 'wijn' : 'wijnen'}`;
    }
  };

  /* ---- stepper -------------------------------------------------------- */

  /** Grootste geldige aantal dat binnen max past, altijd een veelvoud van de stap. */
  const clamp = (value, step, max) => {
    const floored = Math.max(0, value);
    if (max > 0 && floored > max) return Math.floor(max / step) * step;
    return floored;
  };

  list.addEventListener('click', (event) => {
    const stepButton = event.target.closest('[data-delta]');
    if (stepButton) {
      const input = stepButton.parentElement?.querySelector('[data-mijnwijnen-qty]');
      if (!input) return;
      /* De sprong volgt de bestelstap: bij step="6" gaat plus van 0 naar 6
         naar 12, zodat er nooit een ongeldig aantal in het veld staat. */
      const step = Number(input.step) || 1;
      const max = Number(input.max) || 0;
      input.value = String(clamp((Number(input.value) || 0) + Number(stepButton.dataset.delta) * step, step, max));
      refresh();
      return;
    }

    const chip = event.target.closest('[data-mijnwijnen-chip]');
    if (chip) {
      const row = chip.closest('[data-mijnwijnen-row]');
      const input = row?.querySelector('[data-mijnwijnen-qty]');
      if (!input) return;
      /* De chip is een sneltoets naar precies deze drempel. clamp is een
         vangnet: drempels boven het maximum worden al niet gerenderd. */
      const step = Number(input.step) || 1;
      const max = Number(input.max) || 0;
      input.value = String(clamp(Number(chip.dataset.quantity) || 0, step, max));
      refresh();
      chip.focus();
      return;
    }

    const removeButton = event.target.closest('[data-mijnwijnen-remove]');
    if (removeButton) removeRow(removeButton);
  });

  list.addEventListener('input', refresh);

  /* Pijltjesnavigatie zoals een radiogroup zich hoort te gedragen: verplaatsen
     kiest meteen, net als de kaartjes op de productpagina. De klik wordt echt
     verstuurd, zodat het zetten van het aantal op één plek blijft staan. */
  list.addEventListener('keydown', (event) => {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
    if (!keys.includes(event.key)) return;
    const chip = event.target.closest?.('[data-mijnwijnen-chip]');
    if (!chip) return;
    const group = chip.closest('[data-mijnwijnen-chips]');
    if (!group) return;
    const chips = Array.from(group.querySelectorAll('[data-mijnwijnen-chip]'));
    const current = chips.indexOf(chip);
    if (current === -1) return;
    event.preventDefault();
    const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
    const next = (current + (forward ? 1 : -1) + chips.length) % chips.length;
    chips[next].click();
  });

  /* Afronden gebeurt op change (blur), niet tijdens het typen: "24" intypen
     moet kunnen zonder dat het veld onder je handen verspringt. */
  list.addEventListener('change', (event) => {
    const input = event.target.closest('[data-mijnwijnen-qty]');
    if (!input) return;
    const step = Number(input.step) || 1;
    const max = Number(input.max) || 0;
    const value = Math.max(0, Number(input.value) || 0);
    const rounded = clamp(Math.round(value / step) * step, step, max);
    if (rounded !== value) {
      input.value = String(rounded);
      /* De melding hoort bij een echte bestelstap. Bij step 1 is er niets uit
         te leggen (dan corrigeren we hooguit een ingetypt decimaal getal), dus
         daar blijft de live-region stil. */
      if (step > 1) say('notice', `Deze wijn bestelt u per ${step}. Aangepast naar ${rounded}.`);
    }
    refresh();
  });

  /* ---- winkelwagen ---------------------------------------------------- */

  const collectItems = () =>
    rows()
      .filter((row) => !isLeaving(row))
      .map((row) => {
        const input = row.querySelector('[data-mijnwijnen-qty]');
        return input ? { id: Number(input.dataset.variantId), quantity: Number(input.value) || 0 } : null;
      })
      .filter((item) => item && item.quantity > 0);

  /* De cart-count in de header luistert op het cart-update-event van het thema
     (assets/cart-icon.js), dus we halen de verse winkelwagen op en melden hem
     via hetzelfde event in plaats van een eigen mechanisme te verzinnen. */
  const announceCart = async () => {
    let cart = null;
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      if (response.ok) cart = await response.json();
    } catch (_) {
      /* De winkelwagen is bijgewerkt; alleen de teller loopt dan even achter. */
    }
    form.dispatchEvent(
      new CartUpdateEvent(cart ?? {}, 'mijnwijnen-orderform', {
        source: 'mijnwijnen-orderform',
        itemCount: cart?.item_count ?? 0,
      })
    );
  };

  const addToCart = async () => {
    const items = collectItems();
    const bottles = items.reduce((sum, item) => sum + item.quantity, 0);

    /* De knop is nooit disabled: klikken zonder aantallen legt uit welke stap
       ontbreekt, in plaats van stil niets te doen. Geen request. */
    if (bottles === 0) {
      say('notice', 'Vul bij minstens één wijn een aantal in.');
      list.querySelector('[data-mijnwijnen-qty]')?.focus();
      return;
    }

    clearSay();
    submit?.setAttribute('aria-busy', 'true');
    try {
      /* Eén request voor de hele selectie: bij een fout is de winkelwagen dan
         niet half gevuld. */
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error('cart_add_failed');
      await response.json();

      for (const input of list.querySelectorAll('[data-mijnwijnen-qty]')) input.value = '0';
      refresh();
      /* Geen redirect: de lijst is een werkdocument dat vaak in twee rondes
         wordt doorlopen. De bevestiging bevat de link, de klant kiest zelf. */
      say('success', `${bottles} ${bottles === 1 ? 'fles' : 'flessen'} toegevoegd aan uw winkelwagen. `, cartLink());
      await announceCart();
    } catch (_) {
      /* Aantallen blijven staan: niemand moet 30 rijen opnieuw tellen. */
      say(
        'error',
        'Toevoegen is niet gelukt. Uw aantallen staan er nog. ',
        actionButton('Opnieuw proberen', () => form.requestSubmit())
      );
    } finally {
      submit?.removeAttribute('aria-busy');
    }
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    addToCart();
  });

  /* ---- verwijderen ----------------------------------------------------- */

  const showEmptyState = () => {
    if (list.querySelector('[data-mijnwijnen-row]')) return;

    const template = root.querySelector('[data-mijnwijnen-empty-template]');
    if (template instanceof HTMLTemplateElement) {
      form.replaceWith(template.content.cloneNode(true));
      template.remove();
    } else {
      form.remove();
    }
    root.querySelector('.mijnwijnen__toolbar')?.remove();
    root.querySelector('.mijnwijnen__intro')?.remove();

    /* De metaregel gaat mee terug naar de tekst voor een lege lijst. */
    const meta = root.querySelector('.mijnwijnen__meta');
    const company = meta?.querySelector('strong');
    if (meta && company) {
      const name = document.createElement('strong');
      name.textContent = company.textContent;
      meta.replaceChildren('Nog geen wijnen bewaard voor ', name);
    }
  };

  const removeRow = (button) => {
    const row = button.closest('[data-mijnwijnen-row]');
    if (!row) return;
    const productId = row.dataset.productId;
    const name = row.querySelector('.mijnwijnen__name')?.textContent.trim() || 'deze wijn';

    clearSay();
    button.setAttribute('aria-busy', 'true');

    /* De rij verdwijnt direct visueel, maar gaat pas ná het serverantwoord echt
       uit de DOM. Een verborgen placeholder houdt de oorspronkelijke plek vast,
       zodat hij bij een fout terugkomt waar hij stond. */
    row.classList.remove('mijnwijnen__row--returned');
    row.classList.add('mijnwijnen__row--leaving');
    const placeholder = document.createElement('li');
    placeholder.style.display = 'none';
    list.insertBefore(placeholder, row);

    const collapse = setTimeout(() => {
      row.style.display = 'none';
      refresh();
    }, LEAVING_MS);

    removeFromList(productId)
      .then((data) => {
        /* clearTimeout is essentieel: zonder dat verliest een antwoord binnen
           160ms de rij terwijl de melding zegt dat hij terugstaat. */
        clearTimeout(collapse);
        row.remove();
        placeholder.remove();
        notify({ productId, title: name, inList: false, count: data.count });
        refresh();
        showEmptyState();
      })
      .catch(() => {
        clearTimeout(collapse);
        row.style.display = '';
        row.classList.remove('mijnwijnen__row--leaving');
        row.classList.add('mijnwijnen__row--returned');
        row.addEventListener('animationend', () => row.classList.remove('mijnwijnen__row--returned'), { once: true });
        list.insertBefore(row, placeholder);
        placeholder.remove();
        button.removeAttribute('aria-busy');
        refresh();
        say('error', `Verwijderen van ${name} is niet gelukt. De wijn staat weer op de lijst.`);
      });
  };

  /* ---- wissen en sorteren ---------------------------------------------- */

  resetButton?.addEventListener('click', () => {
    for (const input of list.querySelectorAll('[data-mijnwijnen-qty]')) input.value = '0';
    clearSay();
    refresh();
  });

  sortSelect?.addEventListener('change', (event) => {
    const mode = event.target.value;
    const text = (row, selector) => row.querySelector(selector)?.textContent.trim() ?? '';
    const sorted = rows().sort((a, b) => {
      if (mode === 'price') return (Number(a.dataset.price) || 0) - (Number(b.dataset.price) || 0);
      if (mode === 'house') return text(a, '.mijnwijnen__house').localeCompare(text(b, '.mijnwijnen__house'), 'nl');
      if (mode === 'name') return text(a, '.mijnwijnen__name').localeCompare(text(b, '.mijnwijnen__name'), 'nl');
      return (manualOrder.get(a) ?? 0) - (manualOrder.get(b) ?? 0);
    });
    /* Alleen DOM-nodes verplaatsen, zodat ingevulde aantallen bij de juiste
       wijn blijven staan. */
    for (const row of sorted) list.append(row);
  });

  refresh();
};

for (const root of document.querySelectorAll('.mijnwijnen')) init(root);
